
import env from 'consts:environment';


/**
 * @typedef {Record<string,string>} QueryObj
 */
/**
 * @typedef {(settings: InstanceSettings) => QueryObj} QueryFn
 */
/**
 * @typedef {QueryObj | QueryFn} Query
 */


/**
 * @typedef {object} APIEndpointOptions
 * @property {string=} items_prop
 * @property {boolean=} localized
 * @property {string=} name
 * @property {boolean=} priority
 * @property {Query=} query
 * @property {(settings: InstanceSettings, stage: string) => boolean=} skipRequest
 */

class APIEndpoint {

  /**
   * @param {string} api_method
   * @param {APIEndpointOptions} options
   */
  constructor (api_method, options = {}) {
    this.api_method = api_method;
    this.options = options;
    this.items_prop = options.items_prop = api_method;

    const { query = {} } = this.options;
    this.query_param_fn = typeof query == 'function' ? query : () => query;
  }

  /** For calling DS server proxy */
  getProxyURL (settings, lang) {
    const { hash } = settings;
    const search = APIEndpoint.toSearch(lang ? { lang } : {});
    return `/${hash}/api/${this.api_method}${search}`;
  }

  // getProxyURLs :: (Object, Array?) -> [String]
  getProxyURLs (settings, langs, stage) {
    if (this.skipRequest(settings, stage)) {
      return [];
    }
    else if (this.options.localized) {
      if (!langs) throw new Error('No languages provided');
      return langs.map(
        lang => this.getProxyURL(settings, lang)
      );
    }
    else {
      return [this.getProxyURL(settings)];
    }
  }

  /**
   * @typedef {object} Feed
   * @param {string} name 
   * @param {string} lang
   * @param {string} url_client
   */

  /**
   * 
   * @param {object} settings 
   * @param {string[]=} langs 
   * @returns Feed[]
   */
  asFeed (settings, langs) {
    const name = this.options.name || this.api_method;
    const urls = this.getProxyURLs(settings, langs);
    return urls.map((url_client, i) => ({
      url_client,
      name,
      lang: this.options.localized && langs ? langs[i] : false,
    }));
  }

  getHeaders (settings) {
    const { apiKey, defaultApiVersion } = settings;
    return {
      'User-Agent': env.user_agent,
      'x-api-key': apiKey,
      'x-api-version': defaultApiVersion
    };
  }

  getQuery (settings, lang) {
    const { scid, apiKey, defaultApiVersion } = settings;
    const regular = {
      scid,
      apikey: apiKey,
      apiVersion: defaultApiVersion,
      format: 'json'
    };
    const query = this.query_param_fn(settings);
    return Object.assign(lang ? { lang } : {}, query, regular);
  }

  /** For calling API */
  getApiURL (settings, lang) {
    const { apiEndPoint } = settings;
    const query = this.getQuery(settings, lang);
    const search = APIEndpoint.toSearch(query);
    return `${apiEndPoint}/${this.api_method}${search}`;
  }

  /* Returns true if call to API endpoint can be skipped */
  // skipRequest :: (Object, String) - Boolean
  skipRequest (settings, stage) {
    const { skipRequest = APIEndpoint.neverSkip } = this.options;
    return skipRequest(settings, stage);
  }

  static neverSkip () {
    return false;
  }

  static toSearch (query) {
    if (!query) return '';
    const search = Object.entries(query).reduce((acc, [k,v]) => {
      const prefix = acc ? acc + '&' : acc;
      return prefix + `${k}=${encodeURIComponent(v)}`;
    }, '');
    // const search = (new URLSearchParams(query)).toString(); // init w/ object not supported by Fx52
    return search ? `?${search}` : '';
  }

}

export const endpoints = [
  new APIEndpoint('scdata', {
    items_prop: 'shoppingCenterData',
    localized: true,
    priority: true
  }),

  new APIEndpoint('scimddata', {
    items_prop: 'shoppingCenterImdData',
    priority: true
  }),

  new APIEndpoint('stores', {
    name: 'storelist',
    localized: true,
    priority: true,
    query: {
      fields: 'description,images,opening_hours,logo,contact,wayfinding'
    }
  }),

  new APIEndpoint('lunch', {
    name: 'lunchlist'
  }),

  new APIEndpoint('offers', {
    localized: true
  }),

  new APIEndpoint('news', {
    localized: true
  }),

  new APIEndpoint('servicelocations', {
    items_prop: 'serviceLocations',
    skipRequest (settings, stage) {
      const { mapType = {} } = settings;
      return stage != 'service-worker' && !mapType.inhouse;
    },
    query: {
      fields: 'wayfinding'
    }
  }),

  new APIEndpoint('openingHours'),

  new APIEndpoint('media-playlist', {
    skipRequest (settings) {
      const { feature = {} } = settings;
      return !feature.player;
    },
    query (settings) {
      return {
        // scid: settings.scid, // always included
        deviceId: settings.hash
      };
    }
  }),

  new APIEndpoint('imd-banners', {
    items_prop: 'imdBanners',
    priority: true,
  })
];

export const endpoints_map = new Map(
  endpoints.map(x => [x.api_method, x])
);

// filterAPIEndpoints :: (String|[String], Boolean?) -> [APIEndpoint]
export function filterAPIEndpoints (api_methods, include = true) {
  api_methods = [].concat(api_methods);
  const result = endpoints.filter(
    ({ api_method }) => include ?
      api_methods.some(x => x == api_method) :
      api_methods.every(x => x != api_method)
  );

  if (!result.length)
    throw new Error(`No endpoints for api_methods: '${api_methods}', include: '${include}'`);

  return result;
}

/**
 * 
 * @typedef {object} PrioritisedFeeds 
 * @param {Feed[]} priority 
 * @param {Feed[]} secondary  
 */

/**
 * 
 * @param {string} priority_lang 
 * @param {string[]} langs 
 * @param {object} settings
 * @param {{ api_methods: string|string[]; include?: boolean }} filter?
 * @returns PrioritisedFeeds
 */
export function prioritiseFeeds (priority_lang, langs, settings, filter) {

  const endpoints_filtered = filter ? filterAPIEndpoints(filter.api_methods, filter.include) : endpoints;
  const secondary_langs = langs.filter(x => x != priority_lang);

  return endpoints_filtered.reduce((acc, endpoint) => {

    if (endpoint.options.priority) {
      acc.priority = [
        ...acc.priority,
        ...endpoint.asFeed(settings, [priority_lang])
      ];
      if (endpoint.options.localized) {
        acc.secondary = [
          ...acc.secondary,
          ...endpoint.asFeed(settings, secondary_langs)
        ];
      }
    }
    else {
      acc.secondary = [
        ...acc.secondary,
        ...endpoint.asFeed(settings, langs)
      ];
    }
    return acc;
  }, {
    priority: [],
    secondary: []
  });
}
